import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import { cn } from "@/utils/utils";
import { simpleDate } from "@/utils/datetime";
import Calendar from "@/components/ui/calendar";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { isNull } from "lodash";
import Text from "@/components/ui/text";
import { Close } from "@dreamhub/designsystem/icons";
import { Calendar as CalendarIcon } from "lucide-react";
import IconContainer from "@/components/ui/icon-container";
import { IconButton } from "@/components/ui/icon-button";
import { useNewFieldsUI } from "@/services/features/useNewFieldsUI";

// "topFields" keeps the pre-redesign look for the record header, where the date
// sits inline among the title fields rather than in a field row.
export type DateSelectionVariant = "inline" | "form" | "topFields";

interface Props {
  className?: string;
  date: Date | null;
  onDateSelection: (date: Date) => void;
  disabled?: boolean;
  disabledTooltipText?: string;
  availableFrom?: Date;
  availableTo?: Date;
  readOnly?: boolean;
  variant?: DateSelectionVariant;
  placeholder?: string;
  // The clear affordance renders only when a caller supplies this, so a row
  // whose field is required withholds it rather than passing a flag.
  onClear?: () => void;
}

export const DateSelection: React.FC<Props> = ({
  className,
  date,
  onDateSelection,
  disabled = false,
  disabledTooltipText,
  availableFrom,
  availableTo,
  readOnly = false,
  variant = "inline",
  placeholder: placeholderProp,
  onClear
}) => {
  const { t } = useTranslation();
  // Key carries the same English text the untranslated default used, so
  // callers relying on it keep their wording.
  const placeholder = placeholderProp ?? t("selectDate");
  const isNewFieldsUI = useNewFieldsUI();
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const handleOpenChange = (open: boolean) => {
    // `asChild` hands the trigger role to a div, and `disabled` on a div is an
    // inert attribute — it suppresses no pointer events, unlike the button
    // Radix rendered before this component passed asChild. The keydown handler
    // below already checks; this is the same guard for the pointer path.
    if (disabled && open) return;
    setIsPopoverOpen(open);
  };

  const handleSelectDate = (date?: Date) => {
    setIsPopoverOpen(false);
    if (date) {
      return onDateSelection(date);
    }
  };

  const displayValue = isNull(date) ? null : simpleDate(date, "long");
  const canClear =
    isNewFieldsUI && Boolean(displayValue) && !disabled && Boolean(onClear);

  // Read-only is a mode rather than a look: only the redesigned field rows ask
  // for it, and there is no editable rendering to fall back to.
  if (readOnly) {
    return (
      <div className="px-2 py-2 min-h-9 flex items-center">
        <Text variant={displayValue ? "body" : "bodyTertiary"}>
          {displayValue ?? placeholder}
        </Text>
      </div>
    );
  }

  // Same control DateRangeField uses for its clear affordance, so both date
  // fields share one look. It's a real button rather than the trigger's own
  // click handler because it needs to stop that click from reopening/toggling
  // the popover on its way through.
  const renderClearControl = () => (
    <IconButton
      size="xs"
      variant="iconNoBg"
      type="button"
      data-test="date-selection-clear"
      aria-label={t("common.clear")}
      onClick={(clickEvent) => {
        clickEvent.stopPropagation();
        onClear?.();
      }}
      onMouseDown={(mouseEvent) => mouseEvent.preventDefault()}
    >
      <Close />
    </IconButton>
  );

  const renderLegacyTrigger = () => (
    <div
      className={cn(
        "flex gap-1 text-base cursor-pointer rounded-lg hover:bg-surface-hover-strong transition-all duration-300 ease-in-out px-2",
        isPopoverOpen && "bg-surface-hover-strong text-primary"
      )}
    >
      <div
        className={cn(
          "flex p-1 font-normal text-primary min-h-9 h-auto items-center justify-between transition-all duration-300 ease-in-out",
          className
        )}
      >
        {displayValue ?? placeholder}
      </div>
    </div>
  );

  const renderNewTrigger = () => {
    if (variant === "topFields") {
      return (
        <div
          aria-disabled={disabled}
          className={cn(
            "flex gap-1 text-base cursor-pointer rounded-lg hover:bg-surface-hover-strong transition-all duration-300 ease-in-out px-2",
            isPopoverOpen && "bg-surface-hover-strong text-primary",
            disabled && "opacity-50 cursor-not-allowed",
            className
          )}
        >
          <div className="flex gap-1 p-1 min-h-9 h-auto items-center justify-between transition-all duration-300 ease-in-out">
            {/* `body` for both states: the populated value and the placeholder
                rendered the same colour here before, and a tertiary variant
                would change that. */}
            <Text as="span" variant="body">
              {displayValue ?? placeholder}
            </Text>
            {isPopoverOpen && canClear && renderClearControl()}
          </div>
        </div>
      );
    }

    if (variant === "form") {
      return (
        <div
          aria-disabled={disabled}
          className={cn(
            "w-full h-9 pl-3 pr-2 py-[6px] flex items-center justify-between cursor-pointer",
            "bg-surface-field-default border-[0.5px] border-border-field-default rounded-lg",
            "transition-all duration-200 ease-in-out shadow-field",
            isPopoverOpen
              ? "border-border-field-focus bg-surface-focus-selected shadow-field-focus"
              : "hover:border-border-field-hover",
            disabled && "opacity-50 cursor-not-allowed shadow-none",
            className
          )}
        >
          <Text
            as="span"
            variant={displayValue ? "body" : "bodyTertiary"}
            className="leading-none truncate"
          >
            {displayValue || placeholder}
          </Text>
          <div className="flex items-center gap-1 flex-shrink-0">
            {isPopoverOpen && canClear ? (
              renderClearControl()
            ) : !isPopoverOpen ? (
              <IconContainer color="secondary">
                <CalendarIcon size={20} strokeWidth={1.5} />
              </IconContainer>
            ) : null}
          </div>
        </div>
      );
    }

    return (
      <div
        aria-disabled={disabled}
        className={cn(
          "group px-2 py-1 rounded-lg w-full min-h-9 flex items-center transition-all duration-200 ease-in-out",
          isPopoverOpen
            ? "border-[0.5px] border-border-field-focus bg-surface-focus-selected cursor-pointer"
            : "border-[0.5px] border-transparent",
          !isPopoverOpen &&
            !disabled &&
            "hover:bg-surface-hover-soft cursor-pointer",
          // Matches the Select family's inline disabled look: no fill, no hover.
          disabled && "cursor-default",
          className
        )}
      >
        <Text
          as="span"
          variant={displayValue ? "body" : "bodyTertiary"}
          className={cn(
            "truncate flex-1 text-left",
            disabled && "!text-body-disabled"
          )}
        >
          {displayValue || placeholder}
        </Text>
        {isPopoverOpen && canClear && renderClearControl()}
        {!isPopoverOpen && !disabled && (
          <IconContainer
            color="tertiary"
            size="5"
            className="flex-shrink-0 transition-all duration-150 opacity-0 group-hover:opacity-100"
          >
            <CalendarIcon size={20} strokeWidth={1.5} />
          </IconContainer>
        )}
      </div>
    );
  };

  const renderTrigger = () =>
    isNewFieldsUI ? renderNewTrigger() : renderLegacyTrigger();

  const hasDisabledTooltip = Boolean(disabled && disabledTooltipText);

  const popover = (
    <Popover open={isPopoverOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger
        disabled={disabled}
        // Everything below is the redesign's own trigger. `asChild` hands the
        // trigger role to the rendered div, which is neither focusable nor
        // click-on-Enter, so the role/tabIndex/onKeyDown exist to give back
        // what Radix's own button provided. All of it is gated: with the flag
        // off this stays `<PopoverTrigger disabled>` around the legacy trigger,
        // exactly as base renders it, so nothing about the date field changes
        // until the redesign is switched on.
        {...(isNewFieldsUI
          ? {
              asChild: true,
              className: cn(variant !== "topFields" && "w-full"),
              role: "combobox",
              "aria-expanded": isPopoverOpen,
              tabIndex: disabled ? -1 : 0
            }
          : {})}
        onKeyDown={(keyEvent) => {
          if (!isNewFieldsUI) return;
          // The trigger hosts real buttons — the clear control among them — and
          // their Enter/Space belongs to them. Same guard SingleInput and
          // ClickableUrlField use.
          if (keyEvent.target !== keyEvent.currentTarget) return;
          if (
            (keyEvent.key === "Delete" || keyEvent.key === "Backspace") &&
            !isPopoverOpen &&
            canClear
          ) {
            keyEvent.preventDefault();
            onClear?.();
            return;
          }
          if (!disabled && (keyEvent.key === "Enter" || keyEvent.key === " ")) {
            keyEvent.preventDefault();
            setIsPopoverOpen((open) => !open);
          }
        }}
      >
        {/* TooltipTrigger, not Tooltip, has to be the direct child: under
            `asChild` the trigger props are cloned onto whatever sits here, and
            Tooltip's root renders no element to receive them — the trigger lost
            its role, aria-expanded, tabIndex and popover anchor ref. */}
        {hasDisabledTooltip ? (
          <TooltipTrigger asChild>{renderTrigger()}</TooltipTrigger>
        ) : (
          renderTrigger()
        )}
      </PopoverTrigger>
      <PopoverContent
        padding={4}
        className="flex flex-col gap-2 pt-2 w-fit"
        align="start"
        onEscapeKeyDown={() => setIsPopoverOpen(false)}
        sideOffset={4}
        avoidCollisions={true}
      >
        <Calendar
          mode="single"
          selected={date as Date}
          onSelect={(newDate) => handleSelectDate(newDate)}
          defaultMonth={date ? date : new Date()}
          availableFrom={availableFrom}
          availableTo={availableTo}
        />
      </PopoverContent>
    </Popover>
  );

  // The tooltip root wraps the popover rather than sitting inside its trigger.
  // It renders no DOM of its own, so flag-off keeps base's markup exactly.
  return hasDisabledTooltip ? (
    <Tooltip>
      {popover}
      <TooltipContent>{disabledTooltipText}</TooltipContent>
    </Tooltip>
  ) : (
    popover
  );
};

export default DateSelection;
